/**
 * Pure model for the revive sheet: every copy string, the pre-flight
 * resolver that decides whether the primary works, the post-call `CONFLICT`
 * reason mapper, and the request payload builder. No React, no network, so
 * every state is testable without a DOM.
 *
 * `resolveReviveBlock`'s guard order mirrors the server's exact guard order
 * at `postEntryReviveHandler/index.ts` so the pre-flight state and the
 * server verdict never disagree (`plan.md` `## 0.8`, `## 0.9`).
 *
 * `conflictTreatment` never builds a sentence out of the raw `reason`
 * string: `services/contest-api/src/lib/respond.ts` is explicit that a
 * `reason` is never rendered to a fan (`plan.md` `## 0.7`). An unrecognized
 * reason, including a future ninth one, falls to the `default` branch and
 * takes the generic modal rather than throwing or rendering nothing.
 */

import type { useContestGet } from "@sportsmet/contest-sdk/private";

// Derived from the generated contest read rather than restated here, so this
// list cannot drift from the contract and the stored status values are not
// duplicated in app code.
export type typeContestStatus = NonNullable<
  NonNullable<ReturnType<typeof useContestGet>["data"]>["data"]
>["status"];

export const REVIVE_SHEET_CANCEL_LABEL = "Cancel";
export const NO_ELIMINATED_ENTRY_NOTE =
  "This player has no eliminated entry to bring back.";
export const ALREADY_BACK_NOTE = "This entry is already back in the contest.";
export const CONTEST_CONFIRMED_NOTE =
  "This contest is confirmed. Nobody can be revived now.";
export const CONTEST_NOT_RUNNING_NOTE =
  "This contest is not running, so nobody can be revived.";
export const ENTRY_PICKER_PROMPT = "Which entry are you bringing back?";

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

export function reviveSheetTitle(input: { name: string }): string {
  return `Revive ${input.name}?`;
}

export function eliminationLine(input: { roundLabel: string | null }): string {
  return input.roundLabel === null
    ? "Eliminated."
    : `Eliminated in ${input.roundLabel}.`;
}

export function reviveCountsLine(input: {
  usedThisContest: number;
  usedThisSeason: number;
}): string {
  const contestWord = pluralize(input.usedThisContest, "time", "times");
  const seasonWord = pluralize(input.usedThisSeason, "time", "times");
  return `Revived ${input.usedThisContest} ${contestWord} in this contest, ${input.usedThisSeason} ${seasonWord} this season.`;
}

export function reviveBudgetLine(input: {
  revivesAllowed: "unlimited" | "limited";
  revivesAllowedCount: number | null | undefined;
  usedThisContest: number;
}): string {
  if (input.revivesAllowed === "unlimited") {
    return "Unlimited revives in this pool.";
  }
  const allowedCount = input.revivesAllowedCount ?? 0;
  return `${input.usedThisContest} of ${allowedCount} revives used in this contest.`;
}

export function budgetSpentNote(input: {
  name: string;
  allowedCount: number;
}): string {
  const reviveWord = pluralize(input.allowedCount, "revive", "revives");
  return `${input.name} has used all ${input.allowedCount} ${reviveWord} in this contest.`;
}

export function pickRequirementLabel(input: { name: string }): string {
  return `How many picks does ${input.name} owe per round from here?`;
}

export function pickRequirementHelp(input: { name: string }): string {
  return `This replaces ${input.name}'s normal pick count starting with the next round they play. Nobody else's picks change.`;
}

export function revivePrimaryLabel(input: { name: string }): string {
  return `Revive ${input.name}`;
}

export type typeReviveBlock =
  | { kind: "none" }
  | { kind: "noEntry"; note: string }
  | { kind: "alreadyBack"; note: string }
  | { kind: "contestConfirmed"; note: string }
  | { kind: "contestNotRunning"; note: string }
  | { kind: "budgetSpent"; note: string };

export function resolveReviveBlock(input: {
  name: string;
  entryStatus: "in" | "eliminated" | "revived" | null;
  contestStatus: typeContestStatus | null;
  revivesAllowed: "unlimited" | "limited" | null;
  revivesAllowedCount: number | null | undefined;
  usedThisContest: number;
}): typeReviveBlock {
  if (input.entryStatus === null) {
    return { kind: "noEntry", note: NO_ELIMINATED_ENTRY_NOTE };
  }
  if (input.entryStatus !== "eliminated") {
    return { kind: "alreadyBack", note: ALREADY_BACK_NOTE };
  }
  if (input.contestStatus === "confirmed") {
    return { kind: "contestConfirmed", note: CONTEST_CONFIRMED_NOTE };
  }
  if (
    input.contestStatus !== "active" &&
    input.contestStatus !== "pendingOutcome"
  ) {
    return { kind: "contestNotRunning", note: CONTEST_NOT_RUNNING_NOTE };
  }
  if (
    input.revivesAllowed === "limited" &&
    input.usedThisContest >= (input.revivesAllowedCount ?? 0)
  ) {
    return {
      kind: "budgetSpent",
      note: budgetSpentNote({
        name: input.name,
        allowedCount: input.revivesAllowedCount ?? 0,
      }),
    };
  }
  return { kind: "none" };
}

export type typeConflictTreatment =
  { kind: "inline"; note: string } | { kind: "modal" };

export function conflictTreatment(input: {
  reason: string | null;
  name: string;
  allowedCount: number | null | undefined;
}): typeConflictTreatment {
  switch (input.reason) {
    case "entryNotEliminated":
      return { kind: "inline", note: ALREADY_BACK_NOTE };
    case "contestConfirmed":
      return { kind: "inline", note: CONTEST_CONFIRMED_NOTE };
    case "contestNotRevivable":
      return { kind: "inline", note: CONTEST_NOT_RUNNING_NOTE };
    case "reviveBudgetExhausted":
      return {
        kind: "inline",
        note: budgetSpentNote({
          name: input.name,
          allowedCount: input.allowedCount ?? 0,
        }),
      };
    default:
      return { kind: "modal" };
  }
}

// ISSUE-1326. The Commissioner decides what state a revived player comes back
// in (overview.md #8): how many lives, and which used teams they get back.
// Both default to the legacy one-shot revive, one life and every used team
// still spent, so leaving them alone sends nothing extra.

export const DEFAULT_LIVES_GRANTED = 1;

export function livesGrantedLabel(input: { name: string }): string {
  return `How many lives does ${input.name} come back with?`;
}

export function livesGrantedHelp(input: { maxLives: number | null }): string {
  return input.maxLives === null
    ? "One is the usual."
    : `One is the usual. The contest allows up to ${input.maxLives}.`;
}

export function releaseTeamsLabel(input: { name: string }): string {
  return `Give ${input.name} back any used teams?`;
}

export const RELEASE_TEAMS_HELP =
  "A team you check here can be picked again. Leave them all unchecked to keep every used team spent.";

export const RELEASE_TEAMS_NONE_LINE = "No used teams to give back.";

/** Used team ids in the order the entry carries them, each once. */
export function releasableTeamIds(
  usedTeamIds: readonly { teamId: string }[] | undefined,
): string[] {
  return [...new Set((usedTeamIds ?? []).map((used) => used.teamId))];
}

/** The stepper's ceiling: the contest's own lives setting, or none at all. */
export function maxLivesGranted(
  livesAllowed: number | null | undefined,
): number | null {
  return livesAllowed === null ||
    livesAllowed === undefined ||
    livesAllowed < DEFAULT_LIVES_GRANTED
    ? null
    : livesAllowed;
}

/**
 * An untouched lives stepper and an empty release list add no key at all, so
 * a plain revive sends exactly what it always sent and the server applies its
 * own defaults (one life, nothing released).
 */
export function buildRevivePayload(input: {
  poolId: string;
  entryId: string;
  pickRequirement: number;
  contestRequiredPicksPerRound: number;
  livesGranted?: number | undefined;
  teamsToRelease?: readonly string[] | undefined;
}): {
  poolId: string;
  entryId: string;
  requiredPicksOverride: number | null;
  livesGranted?: number;
  teamsToRelease?: string[];
} {
  const livesGranted = input.livesGranted ?? DEFAULT_LIVES_GRANTED;
  const teamsToRelease = [...(input.teamsToRelease ?? [])];
  return {
    poolId: input.poolId,
    entryId: input.entryId,
    requiredPicksOverride:
      input.pickRequirement === input.contestRequiredPicksPerRound
        ? null
        : input.pickRequirement,
    ...(livesGranted === DEFAULT_LIVES_GRANTED ? {} : { livesGranted }),
    ...(teamsToRelease.length === 0 ? {} : { teamsToRelease }),
  };
}
